import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageHeader from '../../components/ui/PageHeader'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useTableSort } from '../../lib/useTableSort'
import MeetingManagementCards from './components/MeetingManagementCards'
import MeetingManagementFilters, {
  type MeetingManagementFiltersValue,
} from './components/MeetingManagementFilters'
import MeetingManagementTable from './components/MeetingManagementTable'
import ReopenTasksDialog from './components/ReopenTasksDialog'
import TaskDecisionButtons from './components/TaskDecisionButtons'
import TaskDetailDialog, { type TaskDetailTarget } from './components/TaskDetailDialog'
import TaskFilesDialog, { type TaskFilesTarget } from './components/TaskFilesDialog'
import {
  useMeetingManagementFontVars,
  useMeetingManagementTasks,
} from './hooks/useMeetingManagement'
import { useTaskDecisionActions } from './hooks/useTaskDecisionActions'
import {
  DEFAULT_STATUS_KEYS,
  flattenTasks,
  type MeetingManagementTask,
  statusQueryOf,
} from './types'
import '../admin/admin.css'
import '../meetings/meetings.css'
import './meetingManagement.css'

/** "Kapat" onay penceresinin grubu — aşağıdaki `<ConfirmDialog group=…>` örneğiyle eşleşir. */
const CLOSE_CONFIRM_GROUP = 'meeting-management-close'

/**
 * Süzgeçlerin varsayılan hâli (FR-006). "Doğrudan Görevler" **işaretli** açılır: kutu eklenmeden önce
 * doğrudan görevler listede zaten vardı, açılış listesi değişmesin.
 */
const DEFAULT_FILTERS: MeetingManagementFiltersValue = {
  statusKeys: DEFAULT_STATUS_KEYS,
  creatorUserId: null,
  mainResponsibleEmployeeId: null,
  supportManagerEmployeeId: null,
  meetingGroupIds: [],
  includeDirectTasks: true,
  includeSubTasks: false,
}

/**
 * <b>Toplantı Yönetimi</b> (105) — tamamlanmış görevlerin toplantı masasında <b>toplu</b> karara
 * bağlandığı ekran: liste süzülür, ana görevler çoklu seçilir, seçim ya tek işlemde <b>kapatılır</b>
 * ya da pop-up'ta tek tek yeni hedef teslim tarihiyle <b>yeniden açılır</b>.
 *
 * <p><b>Başlık = menü adı</b> (anayasa): aynı i18n anahtarından beslenir
 * ({@code nav.tutanak.meetingManagement}); ekrana özel ikinci bir başlık anahtarı <b>açılmaz</b>.
 *
 * <p>Menü kaydı, rotası ve {@code MEETING_MANAGEMENT_VIEW} yetkisi bu işten <b>önce</b> vardı ve boş
 * bir yer tutucu sayfa gösteriyordu; bu bileşen o yer tutucunun yerini alır. Menü/rota/yetki
 * <b>değiştirilmez</b>; uçlar da aynı yetkiyle korunur.
 *
 * <p>Kapat / Yeniden Aç mantığı {@code useTaskDecisionActions}'tadır — aynı karar Onaylarım
 * ekranından (131) da verilir.
 */
export default function MeetingManagementPage() {
  const { t } = useTranslation()
  const isCardView = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const [filters, setFilters] = useState<MeetingManagementFiltersValue>(DEFAULT_FILTERS)
  const [first, setFirst] = useState(0)
  const [size, setSize] = useState(20)
  const decision = useTaskDecisionActions(CLOSE_CONFIRM_GROUP)
  const { readOnly, selection, setSelection } = decision
  /**
   * Dosya pop-up'ının açık olduğu görev; `null` → kapalı. Satırın kendisi değil **asgari şekli**
   * tutulur: liste tazelendiğinde eski satır nesnesi bayatlar, pop-up'ın ihtiyacı olansa yalnız
   * kimlik ve ad.
   */
  const [filesTarget, setFilesTarget] = useState<TaskFilesTarget | null>(null)
  /** Detay pop-up'ının açık olduğu görev (satır tıklaması); `filesTarget` ile aynı asgari-şekil kuralı. */
  const [detailTarget, setDetailTarget] = useState<TaskDetailTarget | null>(null)
  const openDetail = (row: MeetingManagementTask) =>
    setDetailTarget({ id: row.id, subTask: row.subTask })

  // Sıralama değişince ilk sayfaya dön. Açılışta sıralama gönderilmez: sunucu varsayılanı geçerli.
  const sorting = useTableSort(() => setFirst(0))

  const { statusKeys, ...otherFilters } = filters
  const list = useMeetingManagementTasks({
    ...otherFilters,
    ...statusQueryOf(statusKeys),
    page: Math.floor(first / size),
    size,
    sort: sorting.sort,
  })
  const fontVars = useMeetingManagementFontVars()

  // Tablo DÜZ satır ister; sunucu ana görevleri alt görevleriyle İÇ İÇE döndürür (sayfa boyutu ana
  // görev sayar). Düzleştirme yalnız sunumdur — sıra sunucudan gelir, istemci yeniden sıralamaz.
  const rows = useMemo(() => flattenTasks(list.data?.content ?? []), [list.data])
  const totalRecords = list.data?.page.totalElements ?? 0

  /**
   * Süzgeç değişimi: listeyi yeniden sorgula, ilk sayfaya dön ve **seçimi temizle** (FR-019).
   * Seçimi taşımak, kullanıcının artık görmediği satırlar üzerinde işlem yapmasına yol açardı.
   */
  const onFiltersChange = (next: MeetingManagementFiltersValue) => {
    setFilters(next)
    setFirst(0)
    setSelection([])
  }

  const clearFilters = () => onFiltersChange(DEFAULT_FILTERS)

  const hasFilter =
    filters.creatorUserId != null ||
    filters.mainResponsibleEmployeeId != null ||
    filters.supportManagerEmployeeId != null ||
    filters.meetingGroupIds.length > 0 ||
    !filters.includeDirectTasks ||
    filters.includeSubTasks ||
    [...filters.statusKeys].sort().join(',') !== [...DEFAULT_STATUS_KEYS].sort().join(',')

  const onPage = (nextFirst: number, nextRows: number) => {
    setFirst(nextFirst)
    setSize(nextRows)
  }

  return (
    <div className="page meetings-page" style={fontVars}>
      <PageHeader
        title={t('nav.tutanak.meetingManagement')}
        actions={<TaskDecisionButtons actions={decision} />}
      />

      <MeetingManagementFilters
        value={filters}
        onChange={onFiltersChange}
        onClear={clearFilters}
        hasFilter={hasFilter}
      />

      {isCardView ? (
        <MeetingManagementCards
          rows={rows}
          loading={list.isLoading}
          totalRecords={totalRecords}
          first={first}
          rows_={size}
          onPage={onPage}
          selection={selection}
          onToggle={decision.toggleOne}
          selectable={!readOnly}
          onOpenFiles={(row) => setFilesTarget({ id: row.id, name: row.name })}
          onOpenDetail={openDetail}
        />
      ) : (
        <MeetingManagementTable
          rows={rows}
          loading={list.isLoading}
          totalRecords={totalRecords}
          first={first}
          rows_={size}
          onPage={onPage}
          sorting={sorting}
          selection={selection}
          onSelectionChange={setSelection}
          selectable={!readOnly}
          onOpenFiles={(row) => setFilesTarget({ id: row.id, name: row.name })}
          onOpenDetail={openDetail}
        />
      )}

      {/* "Kapat" onayı — `confirmDialog({ group })` çağrısının hedefi; tetiklenmedikçe hiçbir şey
          çizmez (Toplantı Detay ve görev listesindeki kalıbın aynısı). */}
      <ConfirmDialog group={CLOSE_CONFIRM_GROUP} />

      <ReopenTasksDialog
        visible={decision.reopenOpen}
        tasks={selection}
        onHide={() => {
          decision.closeReopen()
          list.refetch()
        }}
      />

      {/* Görev dosyaları (ataç) — salt okunur: indirme + PDF/görsel önizleme. Pop-up SAYFADA durur,
          tablo/kart dalında değil: iki dal aynı pop-up'ı paylaşır ve dar ekrana geçildiğinde açık
          kalan bir pop-up sökülmez. */}
      <TaskFilesDialog task={filesTarget} onHide={() => setFilesTarget(null)} />

      {/* Görev detayı (satır tıklaması) — salt okunur; ana görevde alt görevler yeni sekmede açılır.
          Dosya pop-up'ı gibi SAYFADA durur: tablo ve kart dalı aynı pop-up'ı paylaşır. */}
      <TaskDetailDialog task={detailTarget} onHide={() => setDetailTarget(null)} />
    </div>
  )
}
